import React from 'react';
import dollars from '../../dollar-format';
import Checkbox from "../Checkbox";
import Input from "../Input";
import {CurrentPipeline} from './models/currentPipeline';
import {executeQuery} from "../../requests/executeQuery";
import {HistoricalPipeline} from "./models/historicalPipeline";
import {CurrentPipelineColumns} from "./models/currentPipelineColumns";
import {pipelineTypes, defaultColumns, historicalColumns, quarterlyColumns} from './consts';
import {QuarterlyPipeline} from "./models/quarterlyPipeline";
import arrowDown from "../../svgs/arrow_down.svg";
import arrowRight from "../../svgs/arrow_right.svg";
import {renderPrintFooter} from "../../utils";
import {STRIVE_PIPELINE_V2, STRIVE_PIPELINE_V2_SPLIT, STRIVE_PIPELINE_TRANSACTION_TYPE, STRIVE_PIPELINE_TRANSACTION_TYPE_SPLIT, STRIVE_PIPELINE_CLOSED, STRIVE_PIPELINE_CLOSED_SPLIT, STRIVE_HISTORICAL_PIPELINE, STRIVE_HISTORICAL_PIPELINE_SPLIT, STRIVE_QUARTERLY_PIPELINE, STRIVE_QUARTERLY_PIPELINE_SPLIT} from '../../consts/queries';

export default class Pipeline extends React.Component {
	constructor(props) {
	    super(props);

		this.state = {
            isQuarterlyView: false,
            quarterlyCollapsed: false,
            transactionYear: new Date().getFullYear(),
            pipelineType: pipelineTypes.DEFAULT,
            rows: {},
            breakdownRows: {},
            loading: false,
            isPrint: false,
            printBackground: '',
        };

		this.handleChange = this.handleChange.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.handleCollapse = this.handleCollapse.bind(this);
        this.beforePrint = this.beforePrint.bind(this);
        this.afterPrint = this.afterPrint.bind(this);
	}

	static getQueriesToExecute(type, transactionYear) {
	    switch (type) {
            case pipelineTypes.HISTORICAL:
                return [
                    executeQuery({queryName: STRIVE_HISTORICAL_PIPELINE, params: {TRANSACTION_YEAR: transactionYear}}),
                    executeQuery({queryName: STRIVE_HISTORICAL_PIPELINE_SPLIT, params: {TRANSACTION_YEAR: transactionYear}}),
                ];
            case pipelineTypes.QUARTERLY:
                return [
                    executeQuery({queryName: STRIVE_PIPELINE_CLOSED, params: {TRANSACTION_YEAR: transactionYear}}),
                    executeQuery({queryName: STRIVE_PIPELINE_CLOSED_SPLIT, params: {TRANSACTION_YEAR: transactionYear}}),
                    executeQuery({queryName: STRIVE_QUARTERLY_PIPELINE, params: {QUARTER_START: `${transactionYear}-01-01`, QUARTER_END: `${transactionYear}-03-31`}}),
                    executeQuery({queryName: STRIVE_QUARTERLY_PIPELINE_SPLIT, params: {QUARTER_START: `${transactionYear}-01-01`, QUARTER_END: `${transactionYear}-03-31`}}),
                    executeQuery({queryName: STRIVE_QUARTERLY_PIPELINE, params: {QUARTER_START: `${transactionYear}-04-01`, QUARTER_END: `${transactionYear}-06-30`}}),
                    executeQuery({queryName: STRIVE_QUARTERLY_PIPELINE_SPLIT, params: {QUARTER_START: `${transactionYear}-04-01`, QUARTER_END: `${transactionYear}-06-30`}}),
                    executeQuery({queryName: STRIVE_QUARTERLY_PIPELINE, params: {QUARTER_START: `${transactionYear}-07-01`, QUARTER_END: `${transactionYear}-09-30`}}),
                    executeQuery({queryName: STRIVE_QUARTERLY_PIPELINE_SPLIT, params: {QUARTER_START: `${transactionYear}-07-01`, QUARTER_END: `${transactionYear}-09-30`}}),
                    executeQuery({queryName: STRIVE_QUARTERLY_PIPELINE, params: {QUARTER_START: `${transactionYear}-10-01`, QUARTER_END: `${transactionYear}-12-31`}}),
                    executeQuery({queryName: STRIVE_QUARTERLY_PIPELINE_SPLIT, params: {QUARTER_START: `${transactionYear}-10-01`, QUARTER_END: `${transactionYear}-12-31`}}),
                ];
            default:
                return [
                    executeQuery({queryName: STRIVE_PIPELINE_V2, params: {STATUS: 'Active'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_V2, params: {STATUS: 'In Negotiation'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_V2_SPLIT, params: {STATUS: 'In Negotiation'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_V2, params: {STATUS: 'Under Contract'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_V2_SPLIT, params: {STATUS: 'Under Contract'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_TRANSACTION_TYPE, params: {STATUS_1: 'In Negotiation', STATUS_2: 'Under Contract', TRANSACTION_TYPE_1: 'Advisory', TRANSACTION_TYPE_2: 'Referral'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_TRANSACTION_TYPE_SPLIT, params: {STATUS_1: 'In Negotiation', STATUS_2: 'Under Contract', TRANSACTION_TYPE_1: 'Advisory', TRANSACTION_TYPE_2: 'Referral'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_V2, params: {STATUS: 'Closed'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_V2_SPLIT, params: {STATUS: 'Closed'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_TRANSACTION_TYPE, params: {STATUS_1: 'Closed', STATUS_2: 'NOMATCH', TRANSACTION_TYPE_1: 'Advisory', TRANSACTION_TYPE_2: 'Referral'}}),
                    executeQuery({queryName: STRIVE_PIPELINE_TRANSACTION_TYPE_SPLIT, params: {STATUS_1: 'Closed', STATUS_2: 'NOMATCH', TRANSACTION_TYPE_1: 'Advisory', TRANSACTION_TYPE_2: 'Referral'}}),
                ];
        }
    }

    fetch() {
	    this.setState({loading: true});

	    const transactionYear = this.state.transactionYear;
	    const queries = Pipeline.getQueriesToExecute(this.state.pipelineType, transactionYear);
        Promise.all(queries).then(res => {
            switch (this.state.pipelineType) {
                case pipelineTypes.DEFAULT:
                    if (res && res[0] && res[0].success && Array.isArray(res[0].data) && res[0].data.length &&
                        res[1] && res[1].success && Array.isArray(res[1].data) && res[1].data.length &&
                        res[2] && res[2].success && Array.isArray(res[2].data) && res[2].data.length &&
                        res[3] && res[3].success && Array.isArray(res[3].data) && res[3].data.length &&
                        res[4] && res[4].success && Array.isArray(res[4].data) && res[4].data.length &&
                        res[5] && res[5].success && Array.isArray(res[5].data) && res[5].data.length &&
                        res[6] && res[6].success && Array.isArray(res[6].data) && res[6].data.length &&
                        res[7] && res[7].success && Array.isArray(res[7].data) && res[7].data.length &&
                        res[8] && res[8].success && Array.isArray(res[8].data) && res[8].data.length &&
                        res[9] && res[9].success && Array.isArray(res[9].data) && res[9].data.length &&
                        res[10] && res[10].success && Array.isArray(res[10].data) && res[10].data.length) {

                        const rows = new CurrentPipeline({
                            activeData: res[0].data[0],
                            inNegotiationData: {...res[1].data[0], ...res[2].data[0]},
                            underContractData: {...res[3].data[0], ...res[4].data[0]},
                            inNegUcOtherData: {...res[5].data[0], ...res[6].data[0]},
                            closedYTDData: {...res[7].data[0], ...res[8].data[0]},
                            closedYTDOtherData: {...res[9].data[0], ...res[10].data[0]},
                        });
                        this.setState({rows, loading: false});
                    }
                    break;
                case pipelineTypes.HISTORICAL:
                    if (res && res[0] && res[0].success && Array.isArray(res[0].data) && res[0].data.length &&
                        res[1] && res[1].success && Array.isArray(res[1].data) && res[1].data.length) {
                        const year = transactionYear === new Date().getFullYear() ? null : transactionYear;
                        const data = {...res[0].data[0], ...res[1].data[0]};
                        const rows = [new CurrentPipelineColumns({transactionYear: year, status: 'Closed', data})];
                        let breakdownRows = new HistoricalPipeline({data});
                        this.setState({rows, breakdownRows, loading: false});
                    }
                    break;
                case pipelineTypes.QUARTERLY:
                    if (res && res[0] && res[0].success && Array.isArray(res[0].data) && res[0].data.length &&
                        res[1] && res[1].success && Array.isArray(res[1].data) && res[1].data.length &&
                        res[2] && res[2].success && Array.isArray(res[2].data) && res[2].data.length &&
                        res[3] && res[3].success && Array.isArray(res[3].data) && res[3].data.length &&
                        res[4] && res[4].success && Array.isArray(res[4].data) && res[4].data.length &&
                        res[5] && res[5].success && Array.isArray(res[5].data) && res[5].data.length &&
                        res[6] && res[6].success && Array.isArray(res[6].data) && res[6].data.length &&
                        res[7] && res[7].success && Array.isArray(res[7].data) && res[7].data.length &&
                        res[8] && res[8].success && Array.isArray(res[8].data) && res[8].data.length &&
                        res[9] && res[9].success && Array.isArray(res[9].data) && res[9].data.length) {
                        const status = transactionYear === new Date().getFullYear() ? 'Closed YTD' : 'Closed';
                        const year = transactionYear === new Date().getFullYear() ? null : transactionYear;
                        const rows = [new CurrentPipelineColumns({transactionYear: year, status, data: {...res[0].data[0], ...res[1].data[0]}})];
                        const data = {
                            quarter1Data: {...res[2].data[0], ...res[3].data[0]},
                            quarter2Data: {...res[4].data[0], ...res[5].data[0]},
                            quarter3Data: {...res[6].data[0], ...res[7].data[0]},
                            quarter4Data: {...res[8].data[0], ...res[9].data[0]},
                        };
                        const breakdownRows = new QuarterlyPipeline(data);
                        this.setState({rows, breakdownRows, loading: false});
                    }
                    break;
            }
        });
    }

	componentDidMount() {
        this.setState({printBackground: `url('${arc.path.media+'Transaction_Background.png'}')`}, this.fetch);
        window.onbeforeprint = this.beforePrint;
        window.onafterprint = this.afterPrint;
	}

    beforePrint() {
	    const {isQuarterlyView, breakdownRows} = this.state;
	    if (isQuarterlyView) {
	        const quarterlyCollapsed = !(Object.keys(breakdownRows).filter(quarterKey => !breakdownRows[quarterKey].quarterTotal.childrenCollapsed).length);
            this.setState({quarterlyCollapsed, isPrint: true});
            return;
	    }
        this.setState({quarterlyCollapsed: false, isPrint: true});
    }

    afterPrint() {
        this.setState({isPrint: false});
    }

	handleChange(e) {
        const key = e.currentTarget.name;
        let value = e.currentTarget.value;
        let pipelineType = this.state.pipelineType;
	    const transactionYear = this.state.transactionYear;

        if (key === 'transactionYear') {
            value = parseInt(value);
        }

        if (key === 'isQuarterlyView') {
            value = !this.state[key];
            pipelineType = value ? pipelineTypes.QUARTERLY :
                (transactionYear === new Date().getFullYear() ? pipelineTypes.DEFAULT : pipelineTypes.HISTORICAL);
        }

        this.setState({...this.state, [key]: value, pipelineType}, () => key !== 'transactionYear' && this.fetch());
	}

    handleCollapse(e, key, quarterKey, quarterRow = false) {
	    const breakdownRows = {...this.state.breakdownRows};

	    if (quarterKey) {
            if (quarterRow) {
                if (breakdownRows[key][quarterKey].childrenCollapsed) {
                    breakdownRows[key].retail.collapsed = false;
                    breakdownRows[key].office.collapsed = false;
                    breakdownRows[key].industrial.collapsed = false;
                    breakdownRows[key].other.collapsed = false;
                } else {
                    Object.keys(breakdownRows[key]).map(qKey => {
                        if (qKey !== 'quarterTotal') {
                            breakdownRows[key][qKey].collapsed = true;
                        }
                    });
                }
                breakdownRows[key][quarterKey].childrenCollapsed = !breakdownRows[key][quarterKey].childrenCollapsed;
            } else {
                breakdownRows[key][quarterKey+'STNL'].collapsed = !breakdownRows[key][quarterKey+'STNL'].collapsed;
                breakdownRows[key][quarterKey+'MT'].collapsed = !breakdownRows[key][quarterKey+'MT'].collapsed;
            }
        } else {
            breakdownRows[key+'STNL'].collapsed = !breakdownRows[key+'STNL'].collapsed;
            breakdownRows[key+'MT'].collapsed = !breakdownRows[key+'MT'].collapsed;
        }

        this.setState({breakdownRows});
    }

	onSubmit(e) {
        if (e.key === 'Enter') {
            const {isQuarterlyView, transactionYear} = this.state;
            const key = e.currentTarget.name;
            let pipelineType = this.state.pipelineType;

            if (key === 'transactionYear') {
                pipelineType = isQuarterlyView ? pipelineTypes.QUARTERLY :
                    (transactionYear === new Date().getFullYear() ? pipelineTypes.DEFAULT : pipelineTypes.HISTORICAL);
                this.setState({...this.state, pipelineType}, () => this.fetch());
            }
        }
    }

    renderTable(columns, rows, className, pipelineType) {
	    return (
            <table className={className}>
                <thead>
                <tr>
                    {
                        columns.map((column, index) => {
                            return <th key={index} className="Pipeline_th">{column.name}</th>
                        })
                    }
                </tr>
                </thead>
                <tbody>
                    {
                        Object.keys(rows).map(key => {
                            if (pipelineType === pipelineTypes.QUARTERLY) {
                                return Object.keys(rows[key]).map(quarterKey => {
                                    return (
                                        <tr key={key+'-'+quarterKey}>
                                            {
                                                columns.map((column, index) => {
                                                    if (!rows[key][quarterKey]) return;

                                                    let value = '';
                                                    if (rows[key][quarterKey][column.key] !== null) {
                                                        if (column.format === '$') {
                                                            const withCents = column.withCents;
                                                            value = dollars(rows[key][quarterKey][column.key], withCents);
                                                        } else {
                                                            value = rows[key][quarterKey][column.key];
                                                        }
                                                    }
                                                    return (
                                                        <td key={key+'-'+index+'-'+quarterKey}
                                                            className={(column.class ? column.class : 'Pipeline_td') + (rows[key][quarterKey].collapsed ? ' collapsed' : '') + (rows[key][quarterKey].quarter ? ' bold' : '')}>
                                                            {
                                                                column.key === 'quarter' && rows[key][quarterKey].quarter &&
                                                                <div className='arrow' onClick={(e) => this.handleCollapse(e, key, quarterKey, true)}
                                                                     dangerouslySetInnerHTML={{__html: (rows[key].quarterTotal.childrenCollapsed ? arrowRight : arrowDown)}}/>
                                                            }
                                                            {
                                                                column.key === 'category' && rows[key][quarterKey].category &&
                                                                <div className='arrow' onClick={(e) => this.handleCollapse(e, key, quarterKey)}
                                                                     dangerouslySetInnerHTML={{__html: (rows[key][quarterKey+'STNL'].collapsed ? arrowRight : arrowDown)}}/>
                                                            }
                                                            {value}
                                                        </td>
                                                    )
                                                })
                                            }
                                        </tr>
                                    );
                                })
                            }

                            return (
                                <tr key={key} className={`${key === 'totalClosedYTD' && 'border-top'}`}>
                                    {
                                        columns.map((column, index) => {
                                            let value = '';
                                            if (rows[key][column.key] !== null) {
                                                if (column.format === '$') {
                                                    const withCents = column.withCents;
                                                    value = dollars(rows[key][column.key], withCents);
                                                } else {
                                                    value = rows[key][column.key];
                                                }
                                            }
                                            return (
                                                <td key={key+'-'+index}
                                                    className={(column.class ? column.class : 'Pipeline_td') + (rows[key].collapsed ? ' collapsed' : '') + (rows[key].category ? ' bold' : '')}>
                                                    {
                                                        column.key === 'category' && rows[key].category &&
                                                        <div className='arrow' onClick={(e) => this.handleCollapse(e, key)}
                                                             dangerouslySetInnerHTML={{__html: (rows[key+'STNL'].collapsed ? arrowRight : arrowDown)}}/>
                                                    }
                                                    {value}
                                                </td>
                                            )
                                        })
                                    }
                                </tr>
                            );
                        })
                    }
                </tbody>
            </table>
        );
    }

    static renderQuarterlyPrintTable(columns, rows) {
        return (
            <table className='quarterly'>
                <thead>
                    <tr>
                        {
                            columns.map((column, index) => {
                                return <th key={index} className="Pipeline_th">{column.name}</th>
                            })
                        }
                    </tr>
                </thead>
                <tbody>
                {
                    Object.keys(rows).map(key => {
                        return (
                            <tr key={key}>
                                {
                                    columns.map((column, index) => {
                                        if (!rows[key]) return;

                                        let value = '';
                                        if (rows[key][column.key] !== null) {
                                            if (column.format === '$') {
                                                const withCents = column.withCents;
                                                value = dollars(rows[key][column.key], withCents);
                                            } else {
                                                value = rows[key][column.key];
                                            }
                                        }
                                        return (
                                            <td key={key+'-'+index}
                                                className={(column.class ? column.class : 'Pipeline_td') + (rows[key].collapsed ? ' collapsed' : '') + (rows[key].quarter ? ' bold' : '')}>
                                                {value}
                                            </td>
                                        )
                                    })
                                }
                            </tr>
                        );
                    })
                }
                </tbody>
            </table>
        );
    }

    renderQuarterlyPrint(date) {
	    const {rows, breakdownRows, transactionYear, isPrint, printBackground} = this.state;
        return (
            <div className='print-container-quarterly'>
                {
                    Object.keys(breakdownRows).map((key, index) => {
                        return (
                            <div key={key} className='print-container' style={{backgroundImage: printBackground}}>
                                <div className="Pipeline_head_left">
                                    <h3 className='Pipeline_h3'>STRIVE Pipeline</h3>
                                    <h4 className='Pipeline_h4 transaction-year'>Transaction Year - {transactionYear}</h4>
                                    <h4 className='Pipeline_h4'>{date}</h4>
                                </div>
                                {index === 0 && this.renderTable(defaultColumns, rows, 'default')}
                                {Pipeline.renderQuarterlyPrintTable(quarterlyColumns, breakdownRows[key])}
                                {renderPrintFooter(isPrint)}
                            </div>
                        )
                    })
                }
            </div>
        )
    }

	render() {
		const {rows, breakdownRows, isQuarterlyView, quarterlyCollapsed, transactionYear, pipelineType, loading, isPrint, printBackground} = this.state;
        const d = new Date();
		const date = d.getMonth()+1+'/'+d.getDate()+'/'+d.getFullYear();
		const defaultClass = pipelineType === pipelineTypes.DEFAULT ? '' : 'default';
		const quarterlyClass = !isPrint || isPrint && quarterlyCollapsed ? 'quarterly' : 'quarterly display-none';

		return (
			<div className='Pipeline'>
                <div className={`print-container ${isQuarterlyView && !quarterlyCollapsed ? 'display-none' : ''}`} style={{backgroundImage: isPrint && printBackground}}>
                    <div className='Pipeline_head'>
                        <div className="Pipeline_head_left">
                            <h3 className='Pipeline_h3'>STRIVE Pipeline</h3>
                            <h4 className='Pipeline_h4 transaction-year'>Transaction Year - {transactionYear}</h4>
                            <h4 className='Pipeline_h4'>{date}</h4>
                        </div>
                        <div className='Pipeline_head_right'>
                            <Checkbox name='isQuarterlyView' value={isQuarterlyView ? 'y' : 'n'} onChange={this.handleChange} label='Quarterly Transactions'/>
                            <Input name='transactionYear' value={transactionYear} label='Transaction Year' onChange={this.handleChange} onKeyUp={this.onSubmit} placeholder='YYYY' pattern='([1|2]\d\d\d)'/>
                        </div>
                    </div>
                    {
                        loading ?
                            <div className='loading'>Loading data...</div>
                        :
                        <div>
                            {this.renderTable(defaultColumns, rows, defaultClass)}
                            {
                                pipelineType === pipelineTypes.HISTORICAL &&
                                this.renderTable(historicalColumns, breakdownRows, 'historical', pipelineTypes.HISTORICAL)
                            }
                            {
                                pipelineType === pipelineTypes.QUARTERLY &&
                                this.renderTable(quarterlyColumns, breakdownRows, quarterlyClass, pipelineTypes.QUARTERLY)
                            }
                        </div>
                    }
                    {renderPrintFooter(isPrint)}
                </div>
                {isQuarterlyView && !quarterlyCollapsed && this.renderQuarterlyPrint(date)}
                <div className='preload-print-background' style={{backgroundImage: printBackground}}/>
            </div>
		);
	}
}
